import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronRight, CircleDot, FileText, Gauge,
  Loader2, RotateCcw, Save, Activity, Thermometer, Zap, Fuel, Wind, Timer, Power,
  Wrench, Eye, Upload, FileUp, AlertCircle, Clock, Archive, Database, EyeOff,
} from 'lucide-react';
import { Section } from './helpers';
import { TabLoader } from './TabLoader';
import ScanHistory from './ScanHistory';
import ScanConfidenceReport from './ScanConfidenceReport';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getLiveDataIcon = (pid) => {
  const map = { '0C': Gauge, '0D': Activity, '05': Thermometer, '11': Zap, '2F': Fuel, '0F': Wind, '1F': Timer };
  return map[pid] || Activity;
};

const OBDTab = ({
  canEdit, inspection, liveProgressData, dtcCodes,
  requestingRescan, requestObdRescan,
  onRefresh,
  // 2026-05-27: parameterize the resource family so this same tab can drive
  // both inspection OBD (`apiBase='/inspections'`, default) and service-job
  // OBD (`apiBase='/service-jobs'`) in the Service Ops Modal.
  apiBase = '/inspections',
}) => {
  const [toggling, setToggling] = useState(false);
  const [obdEnabledLocal, setObdEnabledLocal] = useState(null);
  const obdEnabled = obdEnabledLocal !== null ? obdEnabledLocal : inspection?.obd_scanning_enabled !== false;
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiStale, setAiStale] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);
  const [savingReport, setSavingReport] = useState(false);

  // Sync local override when inspection prop changes (e.g., parent refresh)
  useEffect(() => {
    setObdEnabledLocal(null);
  }, [inspection?.obd_scanning_enabled]);

  useEffect(() => {
    if (!inspection?.obd_ai_analysis) return;
    setAiAnalysis(inspection.obd_ai_analysis);
  }, [inspection?.obd_ai_analysis]);

  const triggerAiAnalysis = async () => {
    if (!inspection?.id) return;
    setAiAnalyzing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}${apiBase}/${inspection.id}/obd-ai-analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setAiAnalysis({
          codes_for_report: res.data.codes_for_report,
          real_codes: res.data.real_codes || [],
          likely_codes: res.data.likely_codes || [],
          noise_codes: (res.data.noise_codes || []).concat(
            (res.data.filtered_out_codes || []).map(c => typeof c === 'string' ? { code: c, ai_verdict: 'NOISE' } : c)
          ),
          summary: res.data.summary,
          analyzed_at: new Date().toISOString(),
          source: 'manual_trigger',
        });
        const s = res.data.summary || {};
        toast.success(`AI Analysis complete: ${s.total || s.real_count || 0} verified code${(s.total || s.real_count || 0) !== 1 ? 's' : ''}`);
        setAiStale(false);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleToggleOBD = async (newValue) => {
    if (!inspection?.id) return;
    setToggling(true);
    setObdEnabledLocal(newValue); // Optimistic update
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}${apiBase}/${inspection.id}/obd-scanning`,
        { enabled: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`OBD Scanning ${newValue ? 'enabled' : 'disabled'}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      setObdEnabledLocal(!newValue); // Revert on failure
      toast.error(error.response?.data?.detail || 'Failed to toggle OBD scanning');
    } finally {
      setToggling(false);
    }
  };

  const handleSaveForReport = async () => {
    if (!inspection?.id || verifiedCodes.length === 0) return;
    setSavingReport(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}${apiBase}/${inspection.id}/dtc-report-selection`,
        {
          selected_codes: reportSelectedCodes.map(c => c.code),
          status_filter: enabledCategories,
          hidden_codes: Array.from(hiddenCodes),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const n = reportSelectedCodes.length;
      toast.success(`${n} DTC code${n !== 1 ? 's' : ''} saved for report`);
    } catch (err) {
      toast.error('Failed to save DTC selection');
    } finally {
      setSavingReport(false);
    }
  };

  // Build verified codes list — all codes from the analyzer are confirmed by the vehicle's ECU
  // v1.66 (CRM-side): also compute `statusCategory` used by category-filter UI + preview.
  const verifiedCodes = useMemo(() => {
    if (!aiAnalysis) return [];
    return (aiAnalysis.codes_for_report || []).map(c => {
      // Prefer explicit statusCategory from mechanic app v1.60+;
      // fall back to parsing legacy `status` text.
      const rawCat = (c.statusCategory || '').toLowerCase();
      const rawStatus = String(c.status || '').toLowerCase();
      const cat = rawCat || (
        rawStatus.includes('pending') ? 'pending'
        : rawStatus.includes('active') || rawStatus.includes('confirmed') ? 'active'
        : rawStatus.includes('history') ? 'history'
        : 'stored'
      );
      const isDemoted = !!c.demotedReason || c.confidenceTier === 'demoted';
      // v1.66.6: confidenceTier === 'low' is the mechanic-app's signal for
      // "real ECU code but our DTC dictionary doesn't decode it" (synthetic
      // description like "Powertrain Generic Code P3400"). Route to Demoted
      // bucket so they're hidden by default; mechanics can still tick them
      // back on individually if relevant. Tagged with reason='synthetic_desc'
      // so the badge says "Stale (Unknown DTC)" instead of "Stale (Memory)".
      const isLowConf = c.confidenceTier === 'low';
      // v1.66.7: detect AI fallback content. When the AI doesn't have
      // specific knowledge of a manufacturer code, it writes boilerplate
      // like "This is a manufacturer-specific [communication or internal]
      // fault code…" and reuses the same generic causes/symptoms across
      // 20+ codes. Demote them so the customer report stays clean; the
      // mechanic can still re-enable individually if relevant.
      //
      // EXCEPTION: codes extracted from a manually-uploaded OBD PDF
      // (`source_methods` includes 'pdf_upload') are TRUSTED in full —
      // they came from a real OEM scanner so the code itself is real,
      // even if the AI wrote generic explanation text. Don't demote.
      const expl = String(c.ai_explanation || c.description || '').toLowerCase();
      const fromPdfUpload = Array.isArray(c.source_methods) && c.source_methods.includes('pdf_upload');
      const aiFallbackText = (
        /manufacturer-specific[^.]*?(fault|communication)/.test(expl) ||
        /(^|\s)a?\s*generic[^.]*?(fault|communication)/.test(expl)
      );
      const isAiFallback = aiFallbackText && !fromPdfUpload;
      const effectiveDemoted = isDemoted || isLowConf || isAiFallback;
      const effectiveDemotedReason = c.demotedReason
        || (isLowConf ? 'synthetic_desc' : null)
        || (isAiFallback ? 'ai_fallback' : null);
      return {
        ...c,
        ai_severity: c.ai_severity === 'IMPORTANT' ? 'CRITICAL' : (c.ai_severity === 'CRITICAL' ? 'CRITICAL' : 'MINOR'),
        status_label: cat === 'pending' ? 'Pending'
          : cat === 'active' ? 'Active'
          : cat === 'history' ? 'History'
          : 'Stored',
        statusCategory: cat,
        // Merge history + stored into one UI bucket so mechanics have fewer switches
        // (the distinction is forensic, not operational).
        filterBucket: effectiveDemoted ? 'demoted' : (cat === 'history' || cat === 'stored' ? 'history' : cat),
        isDemoted: effectiveDemoted,
        demotedReason: effectiveDemotedReason,
      };
    });
  }, [aiAnalysis]);

  // ── Category filter (v1.66) ──────────────────────────────────────
  // Four buckets matching what the mechanic app emits:
  //   active | pending | history (incl. stored) | demoted
  // Mechanic can toggle a whole bucket OR uncheck individual codes.
  // Both choices persist to backend `report_selection.{status_filter, hidden_codes}`.
  const CATEGORY_BUCKETS = ['active', 'pending', 'history', 'demoted'];

  const [enabledCategories, setEnabledCategories] = useState({
    active: true, pending: true, history: true, demoted: false,
  });
  const [hiddenCodes, setHiddenCodes] = useState(new Set());
  const [selectionInitialized, setSelectionInitialized] = useState(false);

  // Hydrate state from saved report_selection once per inspection
  useEffect(() => {
    if (selectionInitialized || !inspection?.id) return;
    const sel = inspection?.obd_scan?.report_selection
      || inspection?.obd_results?.report_selection
      || liveProgressData?.obd_scan?.report_selection
      || null;
    if (sel) {
      if (sel.status_filter && typeof sel.status_filter === 'object') {
        setEnabledCategories(prev => ({ ...prev, ...sel.status_filter }));
      }
      if (Array.isArray(sel.hidden_codes) && sel.hidden_codes.length > 0) {
        setHiddenCodes(new Set(sel.hidden_codes));
      }
    }
    setSelectionInitialized(true);
  }, [inspection?.id, inspection?.obd_scan?.report_selection, liveProgressData?.obd_scan?.report_selection, selectionInitialized]);

  // Per-bucket counts (for chip labels)
  const bucketCounts = useMemo(() => {
    const counts = { active: 0, pending: 0, history: 0, demoted: 0 };
    for (const c of verifiedCodes) counts[c.filterBucket] = (counts[c.filterBucket] || 0) + 1;
    return counts;
  }, [verifiedCodes]);

  // Codes selected for the report = in an enabled bucket AND not individually hidden.
  const reportSelectedCodes = useMemo(() => (
    verifiedCodes.filter(c => enabledCategories[c.filterBucket] && !hiddenCodes.has(c.code))
  ), [verifiedCodes, enabledCategories, hiddenCodes]);

  const toggleCategory = (bucket) => {
    setEnabledCategories(prev => ({ ...prev, [bucket]: !prev[bucket] }));
  };
  const toggleCode = (code) => {
    setHiddenCodes(prev => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });
  };

  // Group verified codes by ECU system
  const groupedCodes = useMemo(() => {
    const groups = {};
    verifiedCodes.forEach(c => {
      const system = c.ecuSource || c.ecu_source || (c.code?.startsWith('P') ? 'Engine/Powertrain' : c.code?.startsWith('C') ? 'Chassis/ABS' : c.code?.startsWith('B') ? 'Body' : 'Network/Other');
      if (!groups[system]) groups[system] = { name: system, codes: [], critical: 0, minor: 0 };
      groups[system].codes.push(c);
      if (c.ai_severity === 'CRITICAL') groups[system].critical++;
      else groups[system].minor++;
    });
    return Object.values(groups).sort((a, b) => b.critical - a.critical);
  }, [verifiedCodes]);

  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // PDF fallback upload (admin-only) — Feb 2026: now async with polling.
  // Backend enqueues a job and returns immediately; we poll every 3s until
  // the Gemini extraction completes (typ. 10–60s, sometimes >2 min on
  // 13-page CaRPM reports).
  const fileInputRef = useRef(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfProgress, setPdfProgress] = useState('');   // human-readable status

  const pollObdPdfJob = async (inspectionId, jobId, token, onDone) => {
    const start = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000;     // 5 min hard ceiling
    const INTERVAL_MS = 3000;
    while (Date.now() - start < TIMEOUT_MS) {
      try {
        const r = await axios.get(
          `${API_URL}${apiBase}/${inspectionId}/obd-pdf-job/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
        );
        const job = r.data || {};
        const elapsed = Math.round((Date.now() - start) / 1000);
        if (job.status === 'completed') {
          onDone({ ok: true, job });
          return;
        }
        if (job.status === 'failed') {
          onDone({ ok: false, error: job.error || 'Extraction failed' });
          return;
        }
        // queued | running
        setPdfProgress(`${job.status === 'running' ? 'Extracting' : 'Queued'} · ${elapsed}s elapsed`);
      } catch (e) {
        // transient — keep polling
      }
      await new Promise(res => setTimeout(res, INTERVAL_MS));
    }
    onDone({ ok: false, error: 'Extraction timed out after 5 minutes. Please try again with a smaller PDF.' });
  };

  const handleObdPdfUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // reset so the same file can be re-picked
    if (!file || !inspection?.id) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF must be under 10 MB');
      return;
    }
    setUploadingPdf(true);
    setPdfProgress('Uploading…');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('pdf', file);
      // Step 1: upload — backend returns job_id immediately
      const res = await axios.post(
        `${API_URL}${apiBase}/${inspection.id}/obd-pdf-upload`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          timeout: 30000,  // upload only — extraction runs in background
        }
      );
      const jobId = res.data?.job_id;
      if (!jobId) {
        throw new Error('Server did not return a job id');
      }
      setPdfProgress('Queued · 0s elapsed');
      // Step 2: poll for completion
      await pollObdPdfJob(inspection.id, jobId, token, ({ ok, job, error }) => {
        if (ok) {
          const result = job?.result || {};
          const n = result.dtc_codes_count || 0;
          toast.success(
            n > 0
              ? `Extracted ${n} DTC code${n !== 1 ? 's' : ''} from ${file.name}`
              : `No DTCs found in ${file.name} — modules summary saved`
          );
          if (onRefresh) onRefresh();
        } else {
          toast.error(error || 'PDF extraction failed');
        }
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'PDF upload failed';
      toast.error(msg);
    } finally {
      setUploadingPdf(false);
      setPdfProgress('');
    }
  };

  // Admin-only fallback: the user's role_code is on the JWT but we don't have
  // useAuth here; fall back to the canEdit prop which the parent modal already
  // restricts to admins + inspector roles.
  const showPdfUpload = canEdit;

  // ── Download OBD PDF (May 2026) ─────────────────────────────────
  // POST /api/inspections/{id}/obd-codes/pdf with the current category +
  // hidden-codes selection so the PDF mirrors what's shown in the modal /
  // customer preview. Stream response → blob → trigger browser download.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadObdPdf = async () => {
    if (!inspection?.id) return;
    setDownloadingPdf(true);
    try {
      // Don't override the global Authorization header — axios.defaults
      // already carries the right token from AuthContext (which may have
      // stashed it in either localStorage or sessionStorage).
      const res = await axios.post(
        `${API_URL}${apiBase}/${inspection.id}/obd-codes/pdf`,
        {
          enabled_buckets: enabledCategories,
          hidden_codes: Array.from(hiddenCodes),
        },
        {
          responseType: 'blob',
          timeout: 60000,
        }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const reg = (inspection.car_number || inspection.rc_number || 'VEHICLE').replace(/[^A-Za-z0-9_-]/g, '');
      const date = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `OBD_${reg}_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('OBD PDF downloaded');
    } catch (err) {
      // Server may have returned an error as a Blob (because we requested
      // responseType:'blob'). Read it as text to surface the FastAPI detail.
      let detail = err.message || 'Failed to generate PDF';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          try {
            const j = JSON.parse(text);
            detail = j.detail || text;
          } catch {
            detail = text || detail;
          }
        } catch {
          /* ignore */
        }
      } else if (data?.detail) {
        detail = data.detail;
      }
      toast.error(detail);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <>
      <TabsContent value="obd" className="space-y-3 mt-0">
        {!inspection?.id ? <TabLoader message="Loading OBD data..." /> : (<>
          {/* ── Compact Status Row (v1.66.3) ────────────────────────────
               OBD Scanning toggle · Upload PDF · Scan Status (with Rescan)
               · Download OBD PDF — in one responsive row. Stacks on small screens.
              ────────────────────────────────────────────────────────── */}
          <div className={`grid grid-cols-1 ${showPdfUpload ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3`}>
            {/* 1. OBD Scanning */}
            <div className={`rounded-lg p-3 border-2 transition-colors h-full ${obdEnabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid="obd-scanning-toggle-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${obdEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                    <Power className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">OBD Scanning</p>
                    <p className="text-[11px] text-gray-500 leading-tight">{obdEnabled ? 'Mechanic must complete OBD scan' : 'Mechanic can skip OBD scan'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {toggling && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                  <Switch checked={obdEnabled} onCheckedChange={handleToggleOBD} disabled={toggling || !canEdit} data-testid="obd-scanning-toggle" />
                </div>
              </div>
            </div>

            {/* 2. PDF Fallback Upload — admin-only */}
            {showPdfUpload && (
              <div
                className={`rounded-lg p-3 border-2 transition-colors h-full ${
                  !liveProgressData?.obd_scan?.completed ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-slate-50'
                }`}
                data-testid="obd-pdf-upload-card"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${!liveProgressData?.obd_scan?.completed ? 'bg-purple-600' : 'bg-slate-400'}`}>
                      <FileUp className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">Upload OBD Report PDF</p>
                      <p className="text-[11px] text-gray-600 leading-tight line-clamp-2">
                        {!liveProgressData?.obd_scan?.completed
                          ? 'No scan yet? Upload a PDF — AI will extract DTC codes automatically.'
                          : 'Fallback: re-extract from a fresh PDF (replaces the latest scan).'}
                      </p>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleObdPdfUpload}
                    style={{ display: 'none' }}
                    data-testid="obd-pdf-upload-input"
                  />
                  <Button
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPdf}
                    className={`shrink-0 ${!liveProgressData?.obd_scan?.completed ? 'bg-purple-600 hover:bg-purple-700' : 'bg-slate-600 hover:bg-slate-700'} text-white`}
                    data-testid="obd-pdf-upload-btn"
                  >
                    {uploadingPdf ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" />{pdfProgress || 'Working…'}</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" />Upload PDF</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* 3. Scan Status + Rescan */}
            <div className={`rounded-lg p-3 border-2 transition-colors h-full ${liveProgressData?.obd_scan?.completed ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${liveProgressData?.obd_scan?.completed ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                    <Gauge className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{liveProgressData?.obd_scan?.completed ? 'Scan Done' : 'Pending'}</p>
                    {liveProgressData?.obd_scan?.scanned_at ? (
                      <p className="text-[11px] text-gray-500 leading-tight">{new Date(liveProgressData.obd_scan.scanned_at).toLocaleString()}</p>
                    ) : (
                      <p className="text-[11px] text-gray-500 leading-tight">No scan recorded yet</p>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <Button variant={liveProgressData?.obd_scan?.completed ? 'outline' : 'default'} size="sm" onClick={requestObdRescan} disabled={requestingRescan}
                    className={`shrink-0 ${liveProgressData?.obd_scan?.completed ? '' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    {requestingRescan ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />…</> : <><RotateCcw className="h-3 w-3 mr-1" />Rescan</>}
                  </Button>
                )}
              </div>
            </div>

            {/* 4. Download OBD PDF — exports the currently-selected codes */}
            <div
              className="rounded-lg p-3 border-2 transition-colors h-full border-blue-200 bg-blue-50/40"
              data-testid="obd-pdf-download-card"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 bg-blue-600">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">Download OBD PDF</p>
                    <p className="text-[11px] text-gray-600 leading-tight line-clamp-2">
                      {reportSelectedCodes.length > 0
                        ? `${reportSelectedCodes.length} selected code${reportSelectedCodes.length !== 1 ? 's' : ''} — same as preview`
                        : 'No codes selected — toggle a category below'}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={handleDownloadObdPdf}
                  disabled={downloadingPdf || !inspection?.id}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="obd-pdf-download-btn"
                >
                  {downloadingPdf ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating…</>
                  ) : (
                    <><FileText className="h-4 w-4 mr-2" />Download PDF</>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════
               LIVE SENSOR DATA — v1.66.7: moved above DTC analysis,
               collapsed by default so DTC chips stay the focus.
             ════════════════════════════════════════════════════════ */}
          {liveProgressData?.obd_scan?.data?.live_data && liveProgressData.obd_scan.data.live_data.length > 0 && (
            <Section title="Live Sensor Data" icon={Activity} defaultOpen={false} badge={`${liveProgressData.obd_scan.data.live_data.length} readings`}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="obd-live-data-grid">
                {liveProgressData.obd_scan.data.live_data.map((reading, idx) => {
                  const IconComp = getLiveDataIcon(reading.pid);
                  return (
                    <div key={idx} className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 hover:shadow-sm transition-all" data-testid={`live-data-reading-${reading.pid || idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-7 w-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <IconComp className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-500 font-medium truncate">{reading.name}</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-gray-900">{typeof reading.value === 'number' ? reading.value.toFixed(reading.value % 1 === 0 ? 0 : 1) : reading.value}</span>
                        <span className="text-xs text-gray-400 font-medium">{reading.unit}</span>
                      </div>
                      {reading.pid && <span className="text-[10px] text-gray-400 font-mono mt-1 block">PID: {reading.pid}</span>}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* ════════════════════════════════════════════════════════
               DTC CODE ANALYSIS — SIMPLIFIED VIEW
             ════════════════════════════════════════════════════════ */}
          <Section title="DTC Code Analysis" icon={Zap} defaultOpen={true}
            badge={aiAnalysis ? `${verifiedCodes.length} verified` : 'Not analyzed'}>
            <div className="space-y-3">
              {/* Stale analysis warning */}
              {aiStale && aiAnalysis && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-300" data-testid="ai-stale-banner">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <span>Report source changed. Re-run analysis to update.</span>
                  </div>
                  <Button variant="default" size="sm" onClick={triggerAiAnalysis} disabled={aiAnalyzing}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ai-reanalyze-stale-btn">
                    {aiAnalyzing ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Analyzing...</> : <><Zap className="h-3.5 w-3.5 mr-1.5" />Re-analyze</>}
                  </Button>
                </div>
              )}

              {/* Trigger / Status bar */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="text-sm text-gray-600">
                  {aiAnalysis ? (
                    <span>Analyzed: {new Date(aiAnalysis.analyzed_at).toLocaleString()} ({aiAnalysis.source?.includes('auto') ? 'Auto' : 'Manual'}{aiAnalysis.scan_number ? ` | Scan #${aiAnalysis.scan_number}` : ''})</span>
                  ) : dtcCodes.length > 0 ? (
                    <span>Run analysis to classify {dtcCodes.length} raw codes</span>
                  ) : (
                    <span>No codes to analyze</span>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={triggerAiAnalysis} disabled={aiAnalyzing || !dtcCodes.length}
                  data-testid="ai-analyze-btn" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                  {aiAnalyzing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...</> : <><Zap className="h-4 w-4 mr-2" />{aiAnalysis ? 'Re-analyze' : 'Analyze Codes'}</>}
                </Button>
              </div>

              {/* Verified Codes List */}
              {aiAnalysis && (
                <>
                  {verifiedCodes.length > 0 ? (
                    <>
                      {/* ── Category Filter (v1.66) ──────────────────── */}
                      <CategoryFilterRow
                        bucketCounts={bucketCounts}
                        enabled={enabledCategories}
                        onToggle={toggleCategory}
                        totalSelected={reportSelectedCodes.length}
                        totalAvailable={verifiedCodes.length}
                      />
                      <div className="space-y-2" data-testid="verified-codes-list">
                      {groupedCodes.map((group, gi) => (
                        <div key={gi} className="border rounded-lg overflow-hidden">
                          <div className="bg-slate-100 px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-200 transition-colors"
                            onClick={() => setExpandedGroups(prev => { const s = new Set(prev); s.has(group.name) ? s.delete(group.name) : s.add(group.name); return s; })}>
                            <div className="flex items-center gap-2.5">
                              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expandedGroups.has(group.name) ? '' : '-rotate-90'}`} />
                              <div className="h-8 w-8 rounded-lg bg-slate-700 flex items-center justify-center">
                                <span className="text-white font-bold text-[10px]">{group.name.substring(0, 4).toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="font-semibold text-sm text-gray-900">{group.name}</p>
                                <p className="text-[11px] text-gray-500">{group.codes.length} code{group.codes.length !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              {group.critical > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700 border border-red-200 font-medium">{group.critical} Critical</span>}
                              {group.minor > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-700 border border-sky-200 font-medium">{group.minor} Minor</span>}
                            </div>
                          </div>
                          {expandedGroups.has(group.name) && (
                            <div className="divide-y">
                              {group.codes.map((c, ci) => {
                                const isExpanded = expandedCode === c.code;
                                const hasCauses = c.possible_causes?.length > 0;
                                const hasSymptoms = c.symptoms?.length > 0;
                                const hasActions = c.recommended_actions?.length > 0;
                                const hasDetails = hasCauses || hasSymptoms || hasActions;
                                // v1.66: per-code inclusion state
                                const bucketEnabled = enabledCategories[c.filterBucket];
                                const isHidden = hiddenCodes.has(c.code);
                                const isIncluded = bucketEnabled && !isHidden;
                                return (
                                  <div key={ci} className={`${ci % 2 ? 'bg-gray-50/50' : 'bg-white'} ${!isIncluded ? 'opacity-50' : ''}`} data-testid={`ai-code-${c.code}`}>
                                    <div className="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50">
                                      {/* Inclusion checkbox — disabled when bucket is off */}
                                      <div
                                        className={`mt-1 flex-shrink-0 ${bucketEnabled ? '' : 'pointer-events-none'}`}
                                        onClick={(e) => { e.stopPropagation(); if (bucketEnabled) toggleCode(c.code); }}
                                        title={bucketEnabled ? (isHidden ? 'Click to include this code in the report' : 'Click to hide this code from the report') : 'Bucket is disabled — enable the category above to toggle this code'}
                                        data-testid={`obd-code-include-${c.code}`}
                                      >
                                        <Checkbox
                                          checked={isIncluded}
                                          disabled={!bucketEnabled}
                                          onCheckedChange={() => bucketEnabled && toggleCode(c.code)}
                                        />
                                      </div>
                                      <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer ${c.ai_severity === 'CRITICAL' ? 'bg-red-100' : 'bg-sky-100'}`}
                                        onClick={() => setExpandedCode(isExpanded ? null : c.code)}>
                                        <CircleDot className={`h-3.5 w-3.5 ${c.ai_severity === 'CRITICAL' ? 'text-red-600' : 'text-sky-600'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0 cursor-pointer"
                                        onClick={() => setExpandedCode(isExpanded ? null : c.code)}>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="font-mono font-bold text-slate-800 text-sm">{c.code}</span>
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                            c.ai_severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'
                                          }`}>{c.ai_severity}</span>
                                          <CodeStatusChip category={c.filterBucket} isDemoted={c.isDemoted} demotedReason={c.demotedReason} />
                                          {hasDetails && (
                                            <span className={`inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                                              isExpanded
                                                ? 'bg-blue-100 text-blue-700 border-blue-200'
                                                : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                                            }`}>
                                              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                              {isExpanded ? 'Hide details' : 'Show details'}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5 leading-snug">{c.ai_explanation || c.description || 'No description'}</p>
                                      </div>
                                    </div>
                                    {/* Expandable: Causes, Symptoms, Solutions */}
                                    {isExpanded && hasDetails && (
                                      <div className="px-3 pb-3 pt-0 ml-10 space-y-2">
                                        {hasCauses && (
                                          <DetailBlock title="Possible Causes" items={c.possible_causes} color="amber" icon={AlertTriangle} />
                                        )}
                                        {hasSymptoms && (
                                          <DetailBlock title="Symptoms" items={c.symptoms} color="blue" icon={Eye} />
                                        )}
                                        {hasActions && (
                                          <DetailBlock title="Recommended Actions" items={c.recommended_actions} color="emerald" icon={Wrench} />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 font-medium">No fault codes detected</p>
                      <p className="text-xs text-gray-400 mt-1">All codes were classified as noise or the vehicle is clean</p>
                    </div>
                  )}

                  {/* Save for Report */}
                  {verifiedCodes.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div>
                        <p className="font-medium text-purple-900 text-sm" data-testid="report-selection-count">
                          {reportSelectedCodes.length} of {verifiedCodes.length} code{verifiedCodes.length !== 1 ? 's' : ''} selected for customer report
                        </p>
                        <p className="text-xs text-purple-600">Adjust the category filters above or untick individual codes to refine the customer report.</p>
                      </div>
                      <Button onClick={handleSaveForReport} disabled={savingReport} className="bg-purple-600 hover:bg-purple-700" data-testid="save-report-btn">
                        {savingReport ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save for Report</>}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Section>

          {/* Live Sensor Data */}
          {liveProgressData?.obd_scan?.data?.live_data && liveProgressData.obd_scan.data.live_data.length > 0 && (
            <Section title="Live Sensor Data" icon={Activity} defaultOpen={true} badge={`${liveProgressData.obd_scan.data.live_data.length} readings`}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="obd-live-data-grid">
                {liveProgressData.obd_scan.data.live_data.map((reading, idx) => {
                  const IconComp = getLiveDataIcon(reading.pid);
                  return (
                    <div key={idx} className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 hover:shadow-sm transition-all" data-testid={`live-data-reading-${reading.pid || idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-7 w-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <IconComp className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-500 font-medium truncate">{reading.name}</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-gray-900">{typeof reading.value === 'number' ? reading.value.toFixed(reading.value % 1 === 0 ? 0 : 1) : reading.value}</span>
                        <span className="text-xs text-gray-400 font-medium">{reading.unit}</span>
                      </div>
                      {reading.pid && <span className="text-[10px] text-gray-400 font-mono mt-1 block">PID: {reading.pid}</span>}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Scan Confidence Report */}
          <ScanConfidenceReport inspectionId={inspection?.id} apiBase={apiBase} />

          {/* Scan History */}
          <ScanHistory inspectionId={inspection?.id} onActiveScanChanged={() => setAiStale(true)} apiBase={`/api${apiBase}`} />

          {/* Raw OBD Data */}
          {liveProgressData?.obd_scan?.data && (
            <Section title="Raw OBD Data" icon={FileText} badge="JSON">
              <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-60">
                <pre className="text-xs text-green-400 font-mono">{JSON.stringify(liveProgressData.obd_scan.data, null, 2)}</pre>
              </div>
            </Section>
          )}
        </>)}
      </TabsContent>
    </>
  );
};

/* ── Detail Block for Causes/Symptoms/Actions ── */
const DETAIL_STYLES = {
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', title: 'text-amber-700', dot: 'bg-amber-400' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', title: 'text-blue-700', dot: 'bg-blue-400' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-500', title: 'text-emerald-700', dot: 'bg-emerald-400' },
};

/* ── v1.66 Category Filter Row ──
   Four pills (Active / Pending / History+Stored / Demoted). Each pill is
   a checkbox-style toggle that flips inclusion of every code in that
   bucket. Counts are shown live. Pills hide entirely when the bucket
   is empty so the row stays compact for "1 active code, no others"
   scans.
*/
const BUCKET_META = {
  active: {
    label: 'Active',
    Icon: AlertCircle,
    on: 'bg-red-50 text-red-700 border-red-300',
    off: 'bg-white text-red-400 border-red-200',
    countOn: 'bg-red-600 text-white',
    countOff: 'bg-red-100 text-red-500',
    hint: 'Currently failing — engine light may be on',
  },
  pending: {
    label: 'Pending',
    Icon: Clock,
    on: 'bg-amber-50 text-amber-800 border-amber-300',
    off: 'bg-white text-amber-400 border-amber-200',
    countOn: 'bg-amber-600 text-white',
    countOff: 'bg-amber-100 text-amber-500',
    hint: 'Detected once — will confirm on next drive cycle',
  },
  history: {
    label: 'History / Stored',
    Icon: Archive,
    on: 'bg-slate-100 text-slate-800 border-slate-300',
    off: 'bg-white text-slate-400 border-slate-200',
    countOn: 'bg-slate-600 text-white',
    countOff: 'bg-slate-100 text-slate-500',
    hint: 'Historical or stored codes — not currently failing',
  },
  demoted: {
    label: 'Demoted',
    Icon: EyeOff,
    on: 'bg-gray-100 text-gray-700 border-gray-300',
    off: 'bg-white text-gray-400 border-gray-200',
    countOn: 'bg-gray-500 text-white',
    countOff: 'bg-gray-100 text-gray-500',
    hint: 'ECU memory dump or contradictory pair — likely uncleared history',
  },
};

const CategoryFilterRow = ({ bucketCounts, enabled, onToggle, totalSelected, totalAvailable }) => {
  const buckets = ['active', 'pending', 'history', 'demoted'];
  const visibleBuckets = buckets.filter(b => (bucketCounts[b] || 0) > 0);
  return (
    <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3 sm:p-4" data-testid="obd-category-filter-row">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm font-semibold text-slate-800">
          Show in customer report
          <span className="ml-2 text-xs font-normal text-slate-500">— tap a card to include / exclude. {totalSelected}/{totalAvailable} codes selected.</span>
        </div>
      </div>
      <div className={`grid gap-2 sm:gap-3 ${
        visibleBuckets.length === 1 ? 'grid-cols-1' :
        visibleBuckets.length === 2 ? 'grid-cols-2' :
        visibleBuckets.length === 3 ? 'grid-cols-2 sm:grid-cols-3' :
        'grid-cols-2 sm:grid-cols-4'
      }`}>
        {visibleBuckets.map(b => {
          const count = bucketCounts[b] || 0;
          const meta = BUCKET_META[b];
          const isOn = !!enabled[b];
          const Icon = meta.Icon;
          return (
            <button
              key={b}
              type="button"
              onClick={() => onToggle(b)}
              title={meta.hint}
              data-testid={`obd-category-toggle-${b}`}
              data-state={isOn ? 'on' : 'off'}
              className={`relative flex items-center gap-3 px-3 sm:px-4 py-3 text-sm font-medium border-2 rounded-xl transition-all shadow-sm hover:shadow-md active:scale-[0.98] ${isOn ? meta.on : meta.off}`}
            >
              <span className={`flex items-center justify-center h-5 w-5 rounded-md border-2 flex-shrink-0 ${isOn ? 'bg-slate-800 border-slate-800' : 'bg-white border-slate-300'}`}>
                {isOn && <CheckCircle className="h-4 w-4 text-white" />}
              </span>
              <Icon className={`h-5 w-5 flex-shrink-0 ${isOn ? '' : 'opacity-60'}`} />
              <span className="flex-1 text-left text-sm sm:text-base font-semibold leading-tight">{meta.label}</span>
              <span className={`ml-auto px-2.5 py-1 rounded-full text-sm font-bold min-w-[28px] text-center ${isOn ? meta.countOn : meta.countOff}`}>{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* Per-code status chip — same colour vocabulary as DtcStatusBadge.jsx but
   inline-friendly. Kept local so we can show "Demoted" on demoted codes. */
const CodeStatusChip = ({ category, isDemoted, demotedReason }) => {
  if (isDemoted) {
    let label = 'Stale (Memory)';
    if (demotedReason === 'contradictory_pair') label = 'Stale (Conflict)';
    else if (demotedReason === 'synthetic_desc') label = 'Stale (Unknown DTC)';
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500 border border-gray-300 line-through" title={demotedReason === 'synthetic_desc' ? 'Real ECU code but our DTC dictionary does not decode it (synthetic description)' : 'Demoted by mechanic app heuristic — likely uncleared ECU memory'}>
        {label}
      </span>
    );
  }
  const meta = {
    active: { label: 'Active', cls: 'bg-red-50 text-red-600' },
    pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700' },
    history: { label: 'History', cls: 'bg-slate-50 text-slate-600' },
    stored: { label: 'Stored', cls: 'bg-blue-50 text-blue-600' },
  }[category] || { label: 'Stored', cls: 'bg-blue-50 text-blue-600' };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] ${meta.cls}`}>{meta.label}</span>;
};

const DetailBlock = ({ title, items, color, icon: Icon }) => {
  const s = DETAIL_STYLES[color] || DETAIL_STYLES.blue;
  return (
    <div className={`${s.bg} border ${s.border} rounded-lg p-2.5`} data-testid={`detail-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${s.icon}`} />
        <span className={`text-[11px] font-semibold ${s.title}`}>{title}</span>
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-snug">
            <span className={`${s.dot} h-1.5 w-1.5 rounded-full mt-1.5 flex-shrink-0`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OBDTab;
